"use client";

import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import Link from "next/link";
import {
  X,
  Mail,
  Lock,
  Eye,
  EyeOff,
  User,
  Phone,
  ArrowRight,
  Check,
  CheckCircle,
  Sparkles,
  ArrowLeft,
  ShoppingBag,
  Shield,
  RefreshCw,
} from "lucide-react";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import {
  login,
  signup,
  forgotPassword,
  verifyEmail,
  resendEmailOTP,
  verifyPasswordResetOTP,
  resendPasswordResetOTP,
  resetPassword,
  setTempEmail,
} from "@/store/slices/authSlice";
import AlertDialog from "@/utils/AlertDialog";

export type AuthDrawerMode =
  | "login"
  | "register"
  | "forgot"
  | "verify-email-otp"
  | "verify-reset-otp"
  | "new-password";

interface AuthDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  defaultMode?: AuthDrawerMode;
}

export const openAuthDrawer = (mode: AuthDrawerMode = "login") => {
  if (typeof window !== "undefined") {
    window.dispatchEvent(
      new CustomEvent("open-auth-drawer", { detail: { mode } }),
    );
  }
};

export const AuthDrawer: React.FC<AuthDrawerProps> = ({
  isOpen,
  onClose,
  defaultMode = "login",
}) => {
  const dispatch = useAppDispatch();
  const { loading, tempEmail } = useAppSelector((state) => state.auth);

  const [mode, setMode] = useState<AuthDrawerMode>(defaultMode);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Active email being verified
  const [activeEmail, setActiveEmail] = useState("");

  // Login form state
  const [loginData, setLoginData] = useState({
    email: "",
    password: "",
    rememberMe: true,
  });

  // Register form state
  const [registerData, setRegisterData] = useState({
    fullName: "",
    email: "",
    phone: "",
    password: "",
    confirmPassword: "",
    agreeToTerms: false,
  });

  // Forgot password form state
  const [forgotEmail, setForgotEmail] = useState("");

  // OTP State (6 digits)
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [countdown, setCountdown] = useState(60);
  const [canResend, setCanResend] = useState(false);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  // New Password State (after reset OTP verified)
  const [newPasswordData, setNewPasswordData] = useState({
    password: "",
    confirmPassword: "",
  });

  // Sync mode with defaultMode when opened
  useEffect(() => {
    if (isOpen) {
      setMode(defaultMode);
      setOtp(["", "", "", "", "", ""]);
    }
  }, [isOpen, defaultMode]);

  // Lock body scroll when drawer is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isOpen]);

  // OTP Countdown timer
  useEffect(() => {
    if (mode === "verify-email-otp" || mode === "verify-reset-otp") {
      if (countdown > 0) {
        const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
        return () => clearTimeout(timer);
      } else {
        setCanResend(true);
      }
    }
  }, [countdown, mode]);

  // Reset OTP inputs & focus first box
  const resetOtpForm = () => {
    setOtp(["", "", "", "", "", ""]);
    setCountdown(60);
    setCanResend(false);
    setTimeout(() => {
      inputRefs.current[0]?.focus();
    }, 150);
  };

  // OTP Input Sequencing & Auto-Submit Handler
  const handleOtpChange = (index: number, value: string) => {
    if (value.length > 1) return;
    if (!/^\d*$/.test(value)) return;

    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);

    const fullCode = newOtp.join("");
    // 3. OTP Auto-Submit: when all 6 digits are entered, automatically proceed
    if (fullCode.length === 6 && !newOtp.includes("")) {
      if (mode === "verify-email-otp") {
        handleVerifyEmailOtp(fullCode);
      } else if (mode === "verify-reset-otp") {
        handleVerifyResetOtp(fullCode);
      }
    } else if (value && index < 5) {
      // Focus next box sequentially
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleOtpKeyDown = (
    index: number,
    e: React.KeyboardEvent<HTMLInputElement>,
  ) => {
    if (e.key === "Backspace") {
      if (!otp[index] && index > 0) {
        const newOtp = [...otp];
        newOtp[index - 1] = "";
        setOtp(newOtp);
        inputRefs.current[index - 1]?.focus();
      }
    }
  };

  const handleOtpFocus = (index: number) => {
    // 2. OTP Input Sequencing: ensure focus goes to the first empty box if user tries to skip ahead
    const firstEmptyIndex = otp.findIndex((d) => d === "");
    if (firstEmptyIndex !== -1 && index > firstEmptyIndex) {
      inputRefs.current[firstEmptyIndex]?.focus();
    }
  };

  const handleOtpPaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pastedData = e.clipboardData
      .getData("text")
      .replace(/\D/g, "")
      .slice(0, 6);
    if (!pastedData) return;

    const newOtp = ["", "", "", "", "", ""];
    pastedData.split("").forEach((char, index) => {
      if (index < 6) newOtp[index] = char;
    });
    setOtp(newOtp);

    const fullCode = newOtp.join("");
    if (fullCode.length === 6 && !newOtp.includes("")) {
      if (mode === "verify-email-otp") {
        handleVerifyEmailOtp(fullCode);
      } else if (mode === "verify-reset-otp") {
        handleVerifyResetOtp(fullCode);
      }
    } else {
      const nextIndex = Math.min(pastedData.length, 5);
      inputRefs.current[nextIndex]?.focus();
    }
  };

  // 1. Handle Login Submit
  const handleLoginSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const result = await dispatch(
        login({ email: loginData.email, password: loginData.password }),
      ).unwrap();

      const token = result.accessToken || result.token;
      if (token) {
        document.cookie = `token=${token}; path=/; max-age=${60 * 60 * 24 * 7}; SameSite=Lax`;
      }

      AlertDialog(
        "Success!",
        "Login successful! Welcome back.",
        "success",
        2000,
        false,
        false,
      );
      onClose();
    } catch (error: any) {
      if (error?.requiresEmailVerification) {
        AlertDialog(
          "Verification Required",
          error.message || "Please enter the OTP sent to your email.",
          "warning",
          3000,
          false,
          false,
        );
        setActiveEmail(loginData.email);
        dispatch(setTempEmail(loginData.email));
        setMode("verify-email-otp");
        resetOtpForm();
      } else {
        AlertDialog(
          "Login Failed",
          error?.message ||
            (typeof error === "string" ? error : "Invalid email or password"),
          "error",
          3000,
          false,
          false,
        );
      }
    }
  };

  // 2. Handle Register Submit
  const handleRegisterSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (registerData.password !== registerData.confirmPassword) {
      AlertDialog(
        "Error!",
        "Passwords do not match",
        "error",
        3000,
        false,
        false,
      );
      return;
    }
    if (registerData.password.length < 6) {
      AlertDialog(
        "Error!",
        "Password must be at least 6 characters",
        "error",
        3000,
        false,
        false,
      );
      return;
    }

    try {
      const result = await dispatch(
        signup({
          name: registerData.fullName,
          email: registerData.email,
          password: registerData.password,
          confirmPassword: registerData.confirmPassword,
          phoneNumber: registerData.phone,
          occupation: "Customer",
        }),
      ).unwrap();

      AlertDialog(
        "Verification Code Sent!",
        result?.message || "Please enter the 6-digit OTP sent to your email.",
        "success",
        3000,
        false,
        false,
      );
      setActiveEmail(registerData.email);
      dispatch(setTempEmail(registerData.email));
      setMode("verify-email-otp");
      resetOtpForm();
    } catch (err: any) {
      AlertDialog(
        "Registration Failed",
        err?.message ||
          (typeof err === "string" ? err : "Could not create account"),
        "error",
        3000,
        false,
        false,
      );
    }
  };

  // 3. Handle Forgot Password Submit
  const handleForgotSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!forgotEmail) {
      AlertDialog(
        "Required",
        "Please enter your email address",
        "warning",
        2500,
        false,
        false,
      );
      return;
    }

    try {
      const res = await dispatch(forgotPassword(forgotEmail)).unwrap();
      AlertDialog(
        "OTP Sent!",
        res?.message || "Password reset OTP sent to your email",
        "success",
        3000,
        false,
        false,
      );
      setActiveEmail(forgotEmail);
      dispatch(setTempEmail(forgotEmail));
      setMode("verify-reset-otp");
      resetOtpForm();
    } catch (err: any) {
      AlertDialog(
        "Error",
        err?.message ||
          (typeof err === "string" ? err : "Failed to send reset OTP"),
        "error",
        3000,
        false,
        false,
      );
    }
  };

  // 4. Handle Verify Email OTP (Manual or Auto-Submit)
  const handleVerifyEmailOtp = async (codeToVerify?: string) => {
    const otpValue = codeToVerify || otp.join("");
    if (otpValue.length !== 6) {
      AlertDialog(
        "Incomplete Code",
        "Please enter all 6 digits of the OTP",
        "warning",
        2500,
        false,
        false,
      );
      return;
    }

    const emailToVerify =
      activeEmail || tempEmail || registerData.email || loginData.email;

    try {
      const result = await dispatch(
        verifyEmail({ email: emailToVerify, otp: otpValue }),
      ).unwrap();

      // Auto-login after successful email verification
      const loginEmail = registerData.email || activeEmail || tempEmail || emailToVerify;
      const loginPassword = registerData.password;

      if (loginEmail && loginPassword) {
        try {
          const loginResult = await dispatch(
            login({ email: loginEmail, password: loginPassword }),
          ).unwrap();

          const token = loginResult.accessToken || loginResult.token;
          if (token) {
            document.cookie = `token=${token}; path=/; max-age=${60 * 60 * 24 * 7}; SameSite=Lax`;
          }

          AlertDialog(
            "Welcome!",
            "Your account has been verified and you're now logged in.",
            "success",
            3000,
            false,
            false,
          );
          onClose();
        } catch {
          // Auto-login failed, fall back to login page
          AlertDialog(
            "Email Verified!",
            "Your account has been activated. Please sign in.",
            "success",
            3000,
            false,
            false,
          );
          setMode("login");
        }
      } else {
        AlertDialog(
          "Email Verified!",
          result?.message || "Your account has been activated. Please sign in.",
          "success",
          3000,
          false,
          false,
        );
        setMode("login");
      }
    } catch (error: any) {
      const errorMsg = error?.message || error || "Verification failed";
      AlertDialog("Invalid Code", errorMsg, "error", 3000, false, false);
      setOtp(["", "", "", "", "", ""]);
      setTimeout(() => inputRefs.current[0]?.focus(), 100);
    }
  };

  // 5. Handle Resend Email OTP
  const handleResendEmailOtp = async () => {
    if (!canResend) return;
    const emailToResend =
      activeEmail || tempEmail || registerData.email || loginData.email;
    try {
      const result = await dispatch(resendEmailOTP(emailToResend)).unwrap();
      AlertDialog(
        "Success!",
        result?.message || "A new 6-digit OTP has been sent.",
        "success",
        3000,
        false,
        false,
      );
      resetOtpForm();
    } catch (error: any) {
      AlertDialog(
        "Error!",
        error || "Failed to resend OTP",
        "error",
        3000,
        false,
        false,
      );
    }
  };

  // 6. Handle Verify Password Reset OTP (Manual or Auto-Submit)
  const handleVerifyResetOtp = async (codeToVerify?: string) => {
    const otpValue = codeToVerify || otp.join("");
    if (otpValue.length !== 6) {
      AlertDialog(
        "Incomplete Code",
        "Please enter all 6 digits of the OTP",
        "warning",
        2500,
        false,
        false,
      );
      return;
    }

    const emailToVerify = activeEmail || tempEmail || forgotEmail;

    try {
      const result = await dispatch(
        verifyPasswordResetOTP({ email: emailToVerify, otp: otpValue }),
      ).unwrap();

      AlertDialog(
        "Code Verified!",
        result?.message ||
          "OTP verified successfully. Please enter your new password.",
        "success",
        2500,
        false,
        false,
      );
      setMode("new-password");
    } catch (error: any) {
      const errorMsg = error?.message || error || "Verification failed";
      AlertDialog("Invalid Code", errorMsg, "error", 3000, false, false);
      setOtp(["", "", "", "", "", ""]);
      setTimeout(() => inputRefs.current[0]?.focus(), 100);
    }
  };

  // 7. Handle Resend Password Reset OTP
  const handleResendResetOtp = async () => {
    if (!canResend) return;
    const emailToResend = activeEmail || tempEmail || forgotEmail;
    try {
      const result = await dispatch(
        resendPasswordResetOTP(emailToResend),
      ).unwrap();
      AlertDialog(
        "Success!",
        result?.message || "A new reset code has been sent.",
        "success",
        3000,
        false,
        false,
      );
      resetOtpForm();
    } catch (error: any) {
      AlertDialog(
        "Error!",
        error || "Failed to resend OTP",
        "error",
        3000,
        false,
        false,
      );
    }
  };

  // 8. Handle Set New Password Submit & Auto-Login
  const handleNewPasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (newPasswordData.password !== newPasswordData.confirmPassword) {
      AlertDialog(
        "Error!",
        "Passwords do not match",
        "error",
        3000,
        false,
        false,
      );
      return;
    }
    if (newPasswordData.password.length < 6) {
      AlertDialog(
        "Error!",
        "Password must be at least 6 characters",
        "error",
        3000,
        false,
        false,
      );
      return;
    }

    const targetEmail = (activeEmail || tempEmail || forgotEmail)?.trim();
    if (!targetEmail) {
      AlertDialog(
        "Session Expired",
        "Email address not found. Please re-enter your email in Forgot Password.",
        "warning",
        3000,
        false,
        false,
      );
      setMode("forgot");
      return;
    }

    try {
      // Step 1: Reset the password
      await dispatch(
        resetPassword({
          email: targetEmail,
          newPassword: newPasswordData.password,
          confirmPassword: newPasswordData.confirmPassword,
        }),
      ).unwrap();

      // Step 2: Auto-login immediately with the new password
      try {
        const loginResult = await dispatch(
          login({ email: targetEmail, password: newPasswordData.password }),
        ).unwrap();

        const token = loginResult.accessToken || loginResult.token;
        if (token) {
          document.cookie = `token=${token}; path=/; max-age=${60 * 60 * 24 * 7}; SameSite=Lax`;
        }

        AlertDialog(
          "Success!",
          "Password reset successful! You are now logged in.",
          "success",
          2500,
          false,
          false,
        );
        onClose();
      } catch {
        AlertDialog(
          "Password Reset!",
          "Your password has been reset successfully. Please log in with your new password.",
          "success",
          3000,
          false,
          false,
        );
        setMode("login");
      }
    } catch (error: any) {
      AlertDialog(
        "Error!",
        error?.message ||
          (typeof error === "string" ? error : "Failed to reset password"),
        "error",
        3000,
        false,
        false,
      );
    }
  };

  const isOtpComplete = otp.every((digit) => digit !== "");

  const passwordRequirements = [
    { label: "At least 8 characters", met: registerData.password.length >= 8 },
    { label: "One uppercase letter", met: /[A-Z]/.test(registerData.password) },
    { label: "One number", met: /[0-9]/.test(registerData.password) },
  ];

  const newPasswordRequirements = [
    {
      label: "At least 8 characters",
      met: newPasswordData.password.length >= 8,
    },
    {
      label: "One uppercase letter",
      met: /[A-Z]/.test(newPasswordData.password),
    },
    { label: "One number", met: /[0-9]/.test(newPasswordData.password) },
  ];

  const isMainTabMode = mode === "login" || mode === "register";

  return (
    <AnimatePresence>
      {isOpen && (
        <div
          className="fixed inset-0 z-[100] flex justify-end"
          aria-modal="true"
          role="dialog"
          style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0 }}
        >
          {/* 1. Backdrop Overlay - No outside click dismissal */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm transition-opacity"
          />

          {/* Slide-over Sidebar Drawer */}
          <motion.div
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ type: "spring", damping: 30, stiffness: 300 }}
            className="relative w-full max-w-md md:max-w-lg bg-white h-full shadow-2xl flex flex-col z-10 overflow-hidden text-gray-900"
          >
            {/* ── Drawer Header ── */}
            <div className="flex items-center justify-between px-6 py-5 border-b border-gray-100 bg-white">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-purple-600 to-pink-600 flex items-center justify-center shadow-sm">
                  <ShoppingBag size={16} className="text-white" />
                </div>
                <span className="font-extrabold text-lg tracking-tight bg-clip-text text-transparent bg-gradient-to-r from-purple-600 to-pink-600">
                  Hubmicroo
                </span>
              </div>

              {/* Close Button X */}
              <button
                onClick={onClose}
                className="p-2 rounded-full text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-colors cursor-pointer"
                aria-label="Close auth drawer"
              >
                <X size={22} />
              </button>
            </div>

            {/* ── Mode Navigation Tabs (Login / Register) ── */}
            {isMainTabMode && (
              <div className="flex border-b border-gray-100 bg-gray-50/50">
                <button
                  type="button"
                  onClick={() => setMode("login")}
                  className={`flex-1 py-4 text-xs sm:text-sm font-bold uppercase tracking-wider transition-all relative cursor-pointer ${
                    mode === "login"
                      ? "text-purple-600 bg-white"
                      : "text-gray-500 hover:text-gray-800 hover:bg-gray-100/50"
                  }`}
                >
                  Log In
                  {mode === "login" && (
                    <motion.div
                      layoutId="activeTabIndicator"
                      className="absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r from-purple-600 to-pink-600"
                    />
                  )}
                </button>
                <button
                  type="button"
                  onClick={() => setMode("register")}
                  className={`flex-1 py-4 text-xs sm:text-sm font-bold uppercase tracking-wider transition-all relative cursor-pointer ${
                    mode === "register"
                      ? "text-purple-600 bg-white"
                      : "text-gray-500 hover:text-gray-800 hover:bg-gray-100/50"
                  }`}
                >
                  Create Account
                  {mode === "register" && (
                    <motion.div
                      layoutId="activeTabIndicator"
                      className="absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r from-purple-600 to-pink-600"
                    />
                  )}
                </button>
              </div>
            )}

            {/* ── Drawer Body (Scrollable) ── */}
            <div className="flex-1 overflow-y-auto px-6 py-6 sm:px-8 space-y-6">
              {/* ─────────────────── 1. LOGIN MODE ─────────────────── */}
              {mode === "login" && (
                <motion.div
                  key="login-form"
                  initial={{ opacity: 0, y: 15 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -15 }}
                  transition={{ duration: 0.2 }}
                  className="space-y-6"
                >
                  <div>
                    <h2 className="text-2xl font-normal text-gray-900 tracking-tight">
                      Welcome Back
                    </h2>
                    <p className="text-gray-500 text-sm mt-1">
                      Log in to access your orders, saved items, and fast
                      checkout.
                    </p>
                  </div>

                  <form onSubmit={handleLoginSubmit} className="space-y-4">
                    {/* Email */}
                    <div>
                      <label className="block text-xs font-bold uppercase tracking-wider text-gray-700 mb-1.5">
                        Email Address <span className="text-pink-600">*</span>
                      </label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-gray-400">
                          <Mail size={18} />
                        </div>
                        <input
                          type="email"
                          required
                          value={loginData.email}
                          onChange={(e) =>
                            setLoginData({
                              ...loginData,
                              email: e.target.value,
                            })
                          }
                          placeholder="you@example.com"
                          className="w-full pl-10 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-lg text-gray-900 text-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500/20 focus:border-purple-600 transition-all"
                        />
                      </div>
                    </div>

                    {/* Password */}
                    <div>
                      <div className="flex items-center justify-between mb-1.5">
                        <label className="block text-xs font-bold uppercase tracking-wider text-gray-700">
                          Password <span className="text-pink-600">*</span>
                        </label>
                        <button
                          type="button"
                          onClick={() => setMode("forgot")}
                          className="text-xs font-semibold text-purple-600 hover:text-pink-600 transition-colors cursor-pointer"
                        >
                          Forgot Password?
                        </button>
                      </div>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-gray-400">
                          <Lock size={18} />
                        </div>
                        <input
                          type={showPassword ? "text" : "password"}
                          required
                          value={loginData.password}
                          onChange={(e) =>
                            setLoginData({
                              ...loginData,
                              password: e.target.value,
                            })
                          }
                          placeholder="••••••••"
                          className="w-full pl-10 pr-11 py-3 bg-gray-50 border border-gray-200 rounded-lg text-gray-900 text-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500/20 focus:border-purple-600 transition-all"
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute inset-y-0 right-0 pr-3.5 flex items-center text-gray-400 hover:text-gray-600 cursor-pointer"
                        >
                          {showPassword ? (
                            <EyeOff size={18} />
                          ) : (
                            <Eye size={18} />
                          )}
                        </button>
                      </div>
                    </div>

                    {/* Remember me */}
                    <div className="flex items-center justify-between pt-1">
                      <label className="flex items-center gap-2 cursor-pointer select-none">
                        <input
                          type="checkbox"
                          checked={loginData.rememberMe}
                          onChange={(e) =>
                            setLoginData({
                              ...loginData,
                              rememberMe: e.target.checked,
                            })
                          }
                          className="w-4 h-4 rounded text-purple-600 focus:ring-purple-500 border-gray-300"
                        />
                        <span className="text-xs text-gray-600">
                          Keep me signed in
                        </span>
                      </label>
                    </div>

                    {/* Submit Button */}
                    <motion.button
                      type="submit"
                      disabled={loading}
                      whileHover={{ scale: loading ? 1 : 1.01 }}
                      whileTap={{ scale: loading ? 1 : 0.99 }}
                      className="w-full py-3.5 px-6 rounded-lg font-bold text-white text-sm bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 shadow-md hover:shadow-lg transition-all flex items-center justify-center gap-2 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed mt-4"
                    >
                      {loading ? (
                        <>
                          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                          <span>Signing In...</span>
                        </>
                      ) : (
                        <>
                          <span>SIGN IN</span>
                          <ArrowRight size={16} />
                        </>
                      )}
                    </motion.button>
                  </form>

                  {/* Switch to Register */}
                  <div className="pt-4 border-t border-gray-100 text-center">
                    <p className="text-gray-500 text-xs sm:text-sm">
                      Don't have an account?{" "}
                      <button
                        type="button"
                        onClick={() => setMode("register")}
                        className="font-bold text-purple-600 hover:text-pink-600 underline transition-colors cursor-pointer"
                      >
                        Create an Account
                      </button>
                    </p>
                  </div>
                </motion.div>
              )}

              {/* ─────────────────── 2. REGISTER MODE ─────────────────── */}
              {mode === "register" && (
                <motion.div
                  key="register-form"
                  initial={{ opacity: 0, y: 15 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -15 }}
                  transition={{ duration: 0.2 }}
                  className="space-y-6"
                >
                  <div>
                    <h2 className="text-2xl font-normal text-gray-900 tracking-tight">
                      Create Account
                    </h2>
                    <p className="text-gray-500 text-sm mt-1">
                      Join Hubmicroo for express checkout and exclusive offers.
                    </p>
                  </div>

                  <form onSubmit={handleRegisterSubmit} className="space-y-4">
                    {/* Full Name */}
                    <div>
                      <label className="block text-xs font-bold uppercase tracking-wider text-gray-700 mb-1.5">
                        Full Name <span className="text-pink-600">*</span>
                      </label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-gray-400">
                          <User size={18} />
                        </div>
                        <input
                          type="text"
                          required
                          value={registerData.fullName}
                          onChange={(e) =>
                            setRegisterData({
                              ...registerData,
                              fullName: e.target.value,
                            })
                          }
                          placeholder="Your Name"
                          className="w-full pl-10 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-lg text-gray-900 text-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500/20 focus:border-purple-600 transition-all"
                        />
                      </div>
                    </div>

                    {/* Email */}
                    <div>
                      <label className="block text-xs font-bold uppercase tracking-wider text-gray-700 mb-1.5">
                        Email Address <span className="text-pink-600">*</span>
                      </label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-gray-400">
                          <Mail size={18} />
                        </div>
                        <input
                          type="email"
                          required
                          value={registerData.email}
                          onChange={(e) =>
                            setRegisterData({
                              ...registerData,
                              email: e.target.value,
                            })
                          }
                          placeholder="you@example.com"
                          className="w-full pl-10 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-lg text-gray-900 text-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500/20 focus:border-purple-600 transition-all"
                        />
                      </div>
                    </div>

                    {/* Phone (Optional) */}
                    <div>
                      <label className="block text-xs font-bold uppercase tracking-wider text-gray-700 mb-1.5">
                        Phone Number
                      </label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-gray-400">
                          <Phone size={18} />
                        </div>
                        <input
                          type="tel"
                          value={registerData.phone}
                          onChange={(e) =>
                            setRegisterData({
                              ...registerData,
                              phone: e.target.value,
                            })
                          }
                          placeholder="+92 300 1234567"
                          className="w-full pl-10 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-lg text-gray-900 text-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500/20 focus:border-purple-600 transition-all"
                        />
                      </div>
                    </div>

                    {/* Password */}
                    <div>
                      <label className="block text-xs font-bold uppercase tracking-wider text-gray-700 mb-1.5">
                        Password <span className="text-pink-600">*</span>
                      </label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-gray-400">
                          <Lock size={18} />
                        </div>
                        <input
                          type={showPassword ? "text" : "password"}
                          required
                          value={registerData.password}
                          onChange={(e) =>
                            setRegisterData({
                              ...registerData,
                              password: e.target.value,
                            })
                          }
                          placeholder="••••••••"
                          className="w-full pl-10 pr-11 py-3 bg-gray-50 border border-gray-200 rounded-lg text-gray-900 text-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500/20 focus:border-purple-600 transition-all"
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute inset-y-0 right-0 pr-3.5 flex items-center text-gray-400 hover:text-gray-600 cursor-pointer"
                        >
                          {showPassword ? (
                            <EyeOff size={18} />
                          ) : (
                            <Eye size={18} />
                          )}
                        </button>
                      </div>
                    </div>

                    {/* Confirm Password */}
                    <div>
                      <label className="block text-xs font-bold uppercase tracking-wider text-gray-700 mb-1.5">
                        Confirm Password{" "}
                        <span className="text-pink-600">*</span>
                      </label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-gray-400">
                          <Lock size={18} />
                        </div>
                        <input
                          type={showConfirmPassword ? "text" : "password"}
                          required
                          value={registerData.confirmPassword}
                          onChange={(e) =>
                            setRegisterData({
                              ...registerData,
                              confirmPassword: e.target.value,
                            })
                          }
                          placeholder="••••••••"
                          className="w-full pl-10 pr-11 py-3 bg-gray-50 border border-gray-200 rounded-lg text-gray-900 text-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500/20 focus:border-purple-600 transition-all"
                        />
                        <button
                          type="button"
                          onClick={() =>
                            setShowConfirmPassword(!showConfirmPassword)
                          }
                          className="absolute inset-y-0 right-0 pr-3.5 flex items-center text-gray-400 hover:text-gray-600 cursor-pointer"
                        >
                          {showConfirmPassword ? (
                            <EyeOff size={18} />
                          ) : (
                            <Eye size={18} />
                          )}
                        </button>
                      </div>
                    </div>

                    {/* Requirements checklist */}
                    {registerData.password && (
                      <div className="bg-purple-50/60 border border-purple-100 rounded-lg p-3 space-y-1.5">
                        {passwordRequirements.map((req, i) => (
                          <div key={i} className="flex items-center gap-2">
                            <div
                              className={`w-3.5 h-3.5 rounded-full flex items-center justify-center transition-all ${
                                req.met
                                  ? "bg-emerald-500 text-white"
                                  : "bg-gray-200 text-transparent"
                              }`}
                            >
                              <Check size={10} strokeWidth={3} />
                            </div>
                            <span
                              className={`text-[11px] font-medium ${
                                req.met
                                  ? "text-emerald-700 font-semibold"
                                  : "text-gray-500"
                              }`}
                            >
                              {req.label}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Terms */}
                    <label className="flex items-start gap-2.5 cursor-pointer pt-1">
                      <input
                        type="checkbox"
                        required
                        checked={registerData.agreeToTerms}
                        onChange={(e) =>
                          setRegisterData({
                            ...registerData,
                            agreeToTerms: e.target.checked,
                          })
                        }
                        className="mt-0.5 w-4 h-4 rounded text-purple-600 focus:ring-purple-500 border-gray-300"
                      />
                      <span className="text-xs text-gray-600 leading-relaxed">
                        I agree to the{" "}
                        <Link
                          href="/shop/terms"
                          onClick={onClose}
                          className="text-purple-600 hover:underline font-medium"
                        >
                          Terms of Service
                        </Link>{" "}
                        and{" "}
                        <Link
                          href="/shop/privacy"
                          onClick={onClose}
                          className="text-purple-600 hover:underline font-medium"
                        >
                          Privacy Policy
                        </Link>
                      </span>
                    </label>

                    {/* Submit Button */}
                    <motion.button
                      type="submit"
                      disabled={loading}
                      whileHover={{ scale: loading ? 1 : 1.01 }}
                      whileTap={{ scale: loading ? 1 : 0.99 }}
                      className="w-full py-3.5 px-6 rounded-lg font-bold text-white text-sm bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 shadow-md hover:shadow-lg transition-all flex items-center justify-center gap-2 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed mt-4"
                    >
                      {loading ? (
                        <>
                          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                          <span>Creating Account...</span>
                        </>
                      ) : (
                        <>
                          <span>CREATE ACCOUNT</span>
                          <ArrowRight size={16} />
                        </>
                      )}
                    </motion.button>
                  </form>

                  {/* Switch to Login */}
                  <div className="pt-4 border-t border-gray-100 text-center">
                    <p className="text-gray-500 text-xs sm:text-sm">
                      Already have an account?{" "}
                      <button
                        type="button"
                        onClick={() => setMode("login")}
                        className="font-bold text-purple-600 hover:text-pink-600 underline transition-colors cursor-pointer"
                      >
                        Sign In
                      </button>
                    </p>
                  </div>
                </motion.div>
              )}

              {/* ─────────────────── 3. FORGOT PASSWORD MODE ─────────────────── */}
              {mode === "forgot" && (
                <motion.div
                  key="forgot-form"
                  initial={{ opacity: 0, y: 15 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -15 }}
                  transition={{ duration: 0.2 }}
                  className="space-y-6"
                >
                  <button
                    type="button"
                    onClick={() => setMode("login")}
                    className="inline-flex items-center gap-1.5 text-xs font-bold text-gray-500 hover:text-purple-600 transition-colors uppercase tracking-wider cursor-pointer"
                  >
                    <ArrowLeft size={14} /> Back to Sign In
                  </button>

                  <div>
                    <h2 className="text-2xl font-normal text-gray-900 tracking-tight">
                      Reset Password
                    </h2>
                    <p className="text-gray-500 text-sm mt-1">
                      Enter the email linked with your account and we'll send
                      you an OTP code to reset your password.
                    </p>
                  </div>

                  <form onSubmit={handleForgotSubmit} className="space-y-4">
                    <div>
                      <label className="block text-xs font-bold uppercase tracking-wider text-gray-700 mb-1.5">
                        Email Address <span className="text-pink-600">*</span>
                      </label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-gray-400">
                          <Mail size={18} />
                        </div>
                        <input
                          type="email"
                          required
                          value={forgotEmail}
                          onChange={(e) => setForgotEmail(e.target.value)}
                          placeholder="you@example.com"
                          className="w-full pl-10 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-lg text-gray-900 text-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500/20 focus:border-purple-600 transition-all"
                        />
                      </div>
                    </div>

                    <motion.button
                      type="submit"
                      disabled={loading}
                      whileHover={{ scale: loading ? 1 : 1.01 }}
                      whileTap={{ scale: loading ? 1 : 0.99 }}
                      className="w-full py-3.5 px-6 rounded-lg font-bold text-white text-sm bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 shadow-md hover:shadow-lg transition-all flex items-center justify-center gap-2 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {loading ? (
                        <>
                          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                          <span>Sending OTP...</span>
                        </>
                      ) : (
                        <>
                          <span>SEND RESET CODE</span>
                          <ArrowRight size={16} />
                        </>
                      )}
                    </motion.button>
                  </form>
                </motion.div>
              )}

              {/* ─────────────────── 4. VERIFY EMAIL OTP MODE ─────────────────── */}
              {mode === "verify-email-otp" && (
                <motion.div
                  key="verify-email-form"
                  initial={{ opacity: 0, y: 15 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -15 }}
                  transition={{ duration: 0.2 }}
                  className="space-y-6"
                >
                  <button
                    type="button"
                    onClick={() => setMode("register")}
                    className="inline-flex items-center gap-1.5 text-xs font-bold text-gray-500 hover:text-purple-600 transition-colors uppercase tracking-wider cursor-pointer"
                  >
                    <ArrowLeft size={14} /> Back to Sign Up
                  </button>

                  <div className="text-center">
                    <div className="w-14 h-14 mx-auto rounded-lg bg-purple-50 border border-purple-100 flex items-center justify-center text-purple-600 mb-3 shadow-sm">
                      <Mail size={26} />
                    </div>
                    <h2 className="text-2xl font-normal text-gray-900 tracking-tight">
                      Verify Your Email
                    </h2>
                    <p className="text-gray-500 text-sm mt-1">
                      We sent a 6-digit verification code to
                    </p>
                    <p className="text-purple-600 font-bold text-sm mt-0.5 break-all">
                      {activeEmail ||
                        tempEmail ||
                        registerData.email ||
                        loginData.email}
                    </p>
                  </div>

                  {/* Security Banner */}
                  <div className="bg-purple-50/70 border border-purple-100 rounded-lg p-3 flex items-start gap-2.5">
                    <Shield
                      size={16}
                      className="text-purple-600 flex-shrink-0 mt-0.5"
                    />
                    <p className="text-xs text-gray-600 leading-relaxed">
                      Enter the 6-digit code below. Once all digits are entered,
                      you will be verified automatically.
                    </p>
                  </div>

                  {/* 6 Digit Inputs with Strict Sequencing */}
                  <div>
                    <label className="block text-xs font-bold uppercase tracking-wider text-gray-700 mb-3 text-center">
                      Enter 6-Digit Code
                    </label>
                    <div
                      className="flex justify-center gap-2 sm:gap-2.5"
                      onPaste={handleOtpPaste}
                    >
                      {otp.map((digit, index) => {
                        const isBoxDisabled = index > 0 && !otp[index - 1];
                        return (
                          <input
                            key={index}
                            ref={(el) => {
                              inputRefs.current[index] = el;
                            }}
                            type="text"
                            inputMode="numeric"
                            maxLength={1}
                            disabled={isBoxDisabled}
                            value={digit}
                            onFocus={() => handleOtpFocus(index)}
                            onChange={(e) =>
                              handleOtpChange(index, e.target.value)
                            }
                            onKeyDown={(e) => handleOtpKeyDown(index, e)}
                            className={`w-11 h-13 sm:w-12 sm:h-14 text-center text-xl sm:text-2xl font-extrabold rounded-lg border-2 transition-all outline-none ${
                              isBoxDisabled
                                ? "border-gray-200 bg-gray-100 text-gray-400 cursor-not-allowed opacity-60"
                                : digit
                                  ? "border-purple-600 bg-purple-50/50 text-purple-700"
                                  : "border-gray-200 bg-gray-50 text-gray-800 focus:border-purple-500 focus:bg-white"
                            }`}
                          />
                        );
                      })}
                    </div>
                  </div>

                  {/* Auto-verify Indicator / Button */}
                  <motion.button
                    type="button"
                    onClick={() => handleVerifyEmailOtp()}
                    disabled={!isOtpComplete || loading}
                    whileHover={{ scale: isOtpComplete && !loading ? 1.01 : 1 }}
                    whileTap={{ scale: isOtpComplete && !loading ? 0.99 : 1 }}
                    className={`w-full py-3.5 px-6 rounded-lg font-bold text-sm transition-all flex items-center justify-center gap-2 cursor-pointer shadow-md ${
                      isOtpComplete && !loading
                        ? "bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white hover:shadow-lg"
                        : "bg-gray-100 text-gray-400 cursor-not-allowed"
                    }`}
                  >
                    {loading ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        <span>Verifying...</span>
                      </>
                    ) : (
                      <>
                        <CheckCircle size={16} />
                        <span>
                          {isOtpComplete ? "VERIFYING..." : "ENTER ALL DIGITS"}
                        </span>
                        <ArrowRight size={16} />
                      </>
                    )}
                  </motion.button>

                  {/* Resend OTP */}
                  <div className="text-center pt-2">
                    {countdown > 0 ? (
                      <p className="text-xs text-gray-500">
                        Didn't receive the code?{" "}
                        <span className="font-bold text-purple-600">
                          Resend in {countdown}s
                        </span>
                      </p>
                    ) : (
                      <button
                        type="button"
                        onClick={handleResendEmailOtp}
                        disabled={!canResend || loading}
                        className="inline-flex items-center gap-1.5 text-xs font-bold text-purple-600 hover:text-pink-600 transition-colors cursor-pointer"
                      >
                        <RefreshCw
                          size={13}
                          className={loading ? "animate-spin" : ""}
                        />
                        Resend Verification Code
                      </button>
                    )}
                  </div>
                </motion.div>
              )}

              {/* ─────────────────── 5. VERIFY PASSWORD RESET OTP MODE ─────────────────── */}
              {mode === "verify-reset-otp" && (
                <motion.div
                  key="verify-reset-form"
                  initial={{ opacity: 0, y: 15 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -15 }}
                  transition={{ duration: 0.2 }}
                  className="space-y-6"
                >
                  <button
                    type="button"
                    onClick={() => setMode("forgot")}
                    className="inline-flex items-center gap-1.5 text-xs font-bold text-gray-500 hover:text-purple-600 transition-colors uppercase tracking-wider cursor-pointer"
                  >
                    <ArrowLeft size={14} /> Change Email
                  </button>

                  <div className="text-center">
                    <div className="w-14 h-14 mx-auto rounded-lg bg-purple-50 border border-purple-100 flex items-center justify-center text-purple-600 mb-3 shadow-sm">
                      <Shield size={26} />
                    </div>
                    <h2 className="text-2xl font-normal text-gray-900 tracking-tight">
                      Security Verification
                    </h2>
                    <p className="text-gray-500 text-sm mt-1">
                      We sent a password reset OTP code to
                    </p>
                    <p className="text-purple-600 font-bold text-sm mt-0.5 break-all">
                      {activeEmail || tempEmail || forgotEmail}
                    </p>
                  </div>

                  {/* Security Banner */}
                  <div className="bg-purple-50/70 border border-purple-100 rounded-lg p-3 flex items-start gap-2.5">
                    <Shield
                      size={16}
                      className="text-purple-600 flex-shrink-0 mt-0.5"
                    />
                    <p className="text-xs text-gray-600 leading-relaxed">
                      Enter the 6-digit security code. The form will proceed
                      automatically once all digits are entered.
                    </p>
                  </div>

                  {/* 6 Digit Inputs with Strict Sequencing */}
                  <div>
                    <label className="block text-xs font-bold uppercase tracking-wider text-gray-700 mb-3 text-center">
                      Enter 6-Digit Security Code
                    </label>
                    <div
                      className="flex justify-center gap-2 sm:gap-2.5"
                      onPaste={handleOtpPaste}
                    >
                      {otp.map((digit, index) => {
                        const isBoxDisabled = index > 0 && !otp[index - 1];
                        return (
                          <input
                            key={index}
                            ref={(el) => {
                              inputRefs.current[index] = el;
                            }}
                            type="text"
                            inputMode="numeric"
                            maxLength={1}
                            disabled={isBoxDisabled}
                            value={digit}
                            onFocus={() => handleOtpFocus(index)}
                            onChange={(e) =>
                              handleOtpChange(index, e.target.value)
                            }
                            onKeyDown={(e) => handleOtpKeyDown(index, e)}
                            className={`w-11 h-13 sm:w-12 sm:h-14 text-center text-xl sm:text-2xl font-extrabold rounded-lg border-2 transition-all outline-none ${
                              isBoxDisabled
                                ? "border-gray-200 bg-gray-100 text-gray-400 cursor-not-allowed opacity-60"
                                : digit
                                  ? "border-purple-600 bg-purple-50/50 text-purple-700"
                                  : "border-gray-200 bg-gray-50 text-gray-800 focus:border-purple-500 focus:bg-white"
                            }`}
                          />
                        );
                      })}
                    </div>
                  </div>

                  {/* Verify Button / Auto-Submit Status */}
                  <motion.button
                    type="button"
                    onClick={() => handleVerifyResetOtp()}
                    disabled={!isOtpComplete || loading}
                    whileHover={{ scale: isOtpComplete && !loading ? 1.01 : 1 }}
                    whileTap={{ scale: isOtpComplete && !loading ? 0.99 : 1 }}
                    className={`w-full py-3.5 px-6 rounded-lg font-bold text-sm transition-all flex items-center justify-center gap-2 cursor-pointer shadow-md ${
                      isOtpComplete && !loading
                        ? "bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white hover:shadow-lg"
                        : "bg-gray-100 text-gray-400 cursor-not-allowed"
                    }`}
                  >
                    {loading ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        <span>Verifying...</span>
                      </>
                    ) : (
                      <>
                        <CheckCircle size={16} />
                        <span>
                          {isOtpComplete ? "VERIFYING..." : "ENTER ALL DIGITS"}
                        </span>
                        <ArrowRight size={16} />
                      </>
                    )}
                  </motion.button>

                  {/* Resend OTP */}
                  <div className="text-center pt-2">
                    {countdown > 0 ? (
                      <p className="text-xs text-gray-500">
                        Didn't receive the code?{" "}
                        <span className="font-bold text-purple-600">
                          Resend in {countdown}s
                        </span>
                      </p>
                    ) : (
                      <button
                        type="button"
                        onClick={handleResendResetOtp}
                        disabled={!canResend || loading}
                        className="inline-flex items-center gap-1.5 text-xs font-bold text-purple-600 hover:text-pink-600 transition-colors cursor-pointer"
                      >
                        <RefreshCw
                          size={13}
                          className={loading ? "animate-spin" : ""}
                        />
                        Resend Security Code
                      </button>
                    )}
                  </div>
                </motion.div>
              )}

              {/* ─────────────────── 6. SET NEW PASSWORD MODE & AUTO-LOGIN ─────────────────── */}
              {mode === "new-password" && (
                <motion.div
                  key="new-password-form"
                  initial={{ opacity: 0, y: 15 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -15 }}
                  transition={{ duration: 0.2 }}
                  className="space-y-6"
                >
                  <div>
                    <h2 className="text-2xl font-normal text-gray-900 tracking-tight">
                      Set New Password
                    </h2>
                    <p className="text-gray-500 text-sm mt-1">
                      Create a strong new password. You will be automatically
                      logged in upon saving.
                    </p>
                  </div>

                  <form
                    onSubmit={handleNewPasswordSubmit}
                    className="space-y-4"
                  >
                    {/* New Password */}
                    <div>
                      <label className="block text-xs font-bold uppercase tracking-wider text-gray-700 mb-1.5">
                        New Password <span className="text-pink-600">*</span>
                      </label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-gray-400">
                          <Lock size={18} />
                        </div>
                        <input
                          type={showPassword ? "text" : "password"}
                          required
                          value={newPasswordData.password}
                          onChange={(e) =>
                            setNewPasswordData({
                              ...newPasswordData,
                              password: e.target.value,
                            })
                          }
                          placeholder="••••••••"
                          className="w-full pl-10 pr-11 py-3 bg-gray-50 border border-gray-200 rounded-lg text-gray-900 text-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500/20 focus:border-purple-600 transition-all"
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute inset-y-0 right-0 pr-3.5 flex items-center text-gray-400 hover:text-gray-600 cursor-pointer"
                        >
                          {showPassword ? (
                            <EyeOff size={18} />
                          ) : (
                            <Eye size={18} />
                          )}
                        </button>
                      </div>
                    </div>

                    {/* Confirm New Password */}
                    <div>
                      <label className="block text-xs font-bold uppercase tracking-wider text-gray-700 mb-1.5">
                        Confirm New Password{" "}
                        <span className="text-pink-600">*</span>
                      </label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-gray-400">
                          <Lock size={18} />
                        </div>
                        <input
                          type={showConfirmPassword ? "text" : "password"}
                          required
                          value={newPasswordData.confirmPassword}
                          onChange={(e) =>
                            setNewPasswordData({
                              ...newPasswordData,
                              confirmPassword: e.target.value,
                            })
                          }
                          placeholder="••••••••"
                          className="w-full pl-10 pr-11 py-3 bg-gray-50 border border-gray-200 rounded-lg text-gray-900 text-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500/20 focus:border-purple-600 transition-all"
                        />
                        <button
                          type="button"
                          onClick={() =>
                            setShowConfirmPassword(!showConfirmPassword)
                          }
                          className="absolute inset-y-0 right-0 pr-3.5 flex items-center text-gray-400 hover:text-gray-600 cursor-pointer"
                        >
                          {showConfirmPassword ? (
                            <EyeOff size={18} />
                          ) : (
                            <Eye size={18} />
                          )}
                        </button>
                      </div>
                    </div>

                    {/* Requirements checklist */}
                    {newPasswordData.password && (
                      <div className="bg-purple-50/60 border border-purple-100 rounded-lg p-3 space-y-1.5">
                        {newPasswordRequirements.map((req, i) => (
                          <div key={i} className="flex items-center gap-2">
                            <div
                              className={`w-3.5 h-3.5 rounded-full flex items-center justify-center transition-all ${
                                req.met
                                  ? "bg-emerald-500 text-white"
                                  : "bg-gray-200 text-transparent"
                              }`}
                            >
                              <Check size={10} strokeWidth={3} />
                            </div>
                            <span
                              className={`text-[11px] font-medium ${
                                req.met
                                  ? "text-emerald-700 font-semibold"
                                  : "text-gray-500"
                              }`}
                            >
                              {req.label}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Submit Button */}
                    <motion.button
                      type="submit"
                      disabled={loading}
                      whileHover={{ scale: loading ? 1 : 1.01 }}
                      whileTap={{ scale: loading ? 1 : 0.99 }}
                      className="w-full py-3.5 px-6 rounded-lg font-bold text-white text-sm bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 shadow-md hover:shadow-lg transition-all flex items-center justify-center gap-2 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed mt-4"
                    >
                      {loading ? (
                        <>
                          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                          <span>Saving & Logging In...</span>
                        </>
                      ) : (
                        <>
                          <Lock size={16} />
                          <span>SAVE PASSWORD & LOG IN</span>
                          <ArrowRight size={16} />
                        </>
                      )}
                    </motion.button>
                  </form>
                </motion.div>
              )}
            </div>

            {/* ── Drawer Footer / Brand Note ── */}
            <div className="px-6 py-4 border-t border-gray-100 bg-gray-50/70 flex items-center justify-center gap-2 text-xs text-gray-500">
              <Sparkles size={14} className="text-purple-600" />
              <span>Safe & Secure 256-bit SSL Encrypted</span>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default AuthDrawer;
